import React, { useMemo, useState } from "react";
import { Box, Card, CardContent, Typography, Tabs, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ResponsiveContainer, Sankey, Tooltip as RTooltip } from "recharts";

import RxGhgAccordion from "./RxGhgAccordion";

export default function SankeyPanel({
  sankeyData,
  darkMode,
  unitLabel,
  sankeyQtyRef,
  sankeyAvoidedRef,

  // parent can expand/collapse this accordion during export
  sankeyAccordionRef,
}) {
  const { t } = useTranslation();
  const [active, setActive] = useState("qty");

  /* ---------- HELPERS ---------- */
  const round2 = (v) => (typeof v === "number" ? Number(v.toFixed(2)) : v);

  const PALETTE = useMemo(
    () => [
      "#1f77b4",
      "#ff7f0e",
      "#2ca02c",
      "#d62728",
      "#9467bd",
      "#8c564b",
      "#e377c2",
      "#7f7f7f",
      "#bcbd22",
      "#17becf",
    ],
    []
  );

  const hashString = (s) => {
    let h = 0;
    const str = String(s ?? "");
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0;
    return Math.abs(h);
  };

  const hexToRgba = (hex, a) => {
    const h = String(hex || "").replace("#", "");
    if (h.length !== 6) return `rgba(100,100,100,${a})`;
    return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(
      h.slice(2, 4),
      16
    )},${parseInt(h.slice(4, 6), 16)},${a})`;
  };

  const isImpactNodeName = (n) =>
    n === t("ghg.sankey.quantity", "Quantity") ||
    String(n ?? "").startsWith(
      t("ghg.sankey.avoided", "Avoided ({{unit}})", { unit: unitLabel }).split("{{")[0] // defensive
    ) ||
    String(n ?? "").startsWith("Avoided ("); // fallback for legacy node labels

  const materialFromLinkPayload = (p) => {
    const s = p?.source?.name ?? "";
    const tt = p?.target?.name ?? "";
    if (!isImpactNodeName(tt) && !isImpactNodeName(s)) return tt;
    if (isImpactNodeName(tt) && !isImpactNodeName(s)) return s;
    return tt || s || t("common.unknown", "Unknown");
  };

  const materialColor = (m) => PALETTE[hashString(m) % PALETTE.length];

  const textColor = darkMode ? "#e6eef5" : "#1b2a36";
  const nodeFill = darkMode ? "#24313a" : "#e8eef3";
  const nodeStroke = darkMode ? "#3c4f5c" : "#c2d2dd";

  const reverseSankeyData = (data) => {
    if (!data?.nodes?.length || !data?.links?.length) return data;
    return {
      ...data,
      links: data.links.map((l) => ({
        ...l,
        source: l.target,
        target: l.source,
      })),
    };
  };

  const empty =
    !sankeyData?.qty?.links?.length && !sankeyData?.avoided?.links?.length;

  const qtyDataReversed = useMemo(
    () => reverseSankeyData(sankeyData?.qty),
    [sankeyData]
  );
  const avoidedDataReversed = useMemo(
    () => reverseSankeyData(sankeyData?.avoided),
    [sankeyData]
  );

  /* ---------- NODE ---------- */
  const SankeyNode = ({ x, y, width, height, payload }) => {
    if (height < 8) return null;

    const name = payload?.name ?? "";
    const showLabel = height > 16;
    const label = name.length > 22 ? `${name.slice(0, 22)}…` : name;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={Math.max(2, width)}
          height={height}
          rx={2}
          fill={isImpactNodeName(name) ? "#1e3a4b" : nodeFill}
          stroke={nodeStroke}
        >
          <title>{name}</title>
        </rect>

        {showLabel && (
          <text
            x={x + width / 2}
            y={y + height / 2}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={12}
            fontWeight={600}
            fill={textColor}
            style={{ pointerEvents: "none" }}
          >
            {label}
          </text>
        )}
      </g>
    );
  };

  /* ---------- LINK ---------- */
  const SankeyLink = ({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourceControlX,
    targetControlX,
    linkWidth,
    payload,
  }) => {
    if (linkWidth < 0.5) return null;

    const fill = hexToRgba(
      materialColor(materialFromLinkPayload(payload)),
      darkMode ? 0.6 : 0.45
    );

    const w = Math.max(1, linkWidth);
    const y0t = sourceY - w / 2;
    const y0b = sourceY + w / 2;
    const y1t = targetY - w / 2;
    const y1b = targetY + w / 2;

    const d = `
      M ${sourceX},${y0t}
      C ${sourceControlX},${y0t} ${targetControlX},${y1t} ${targetX},${y1t}
      L ${targetX},${y1b}
      C ${targetControlX},${y1b} ${sourceControlX},${y0b} ${sourceX},${y0b}
      Z
    `;

    return <path d={d} fill={fill} />;
  };

  return (
    <Box className="rxghg_block">
      <RxGhgAccordion
        ref={sankeyAccordionRef}
        id="rxghg_sankey"
        title={t(
          "ghg.sankeyPanel.title",
          "Sankey breakdown (Impact → Materials → Clients)"
        )}
        subtitle={t(
          "ghg.sankeyPanel.subtitle",
          "Reversed view so Quantity/Avoided starts on the left."
        )}
      >
        {empty ? (
          <Typography sx={{ opacity: 0.7 }}>
            {t(
              "ghg.common.noDeliveredDataInRange",
              "No delivered data for the selected range."
            )}
          </Typography>
        ) : (
          <Card variant="outlined">
            <CardContent>
              <Tabs value={active} onChange={(_, v) => setActive(v)} sx={{ mb: 2 }}>
                <Tab value="qty" label={t("ghg.sankeyPanel.tabs.qty", "Quantity flow")} />
                <Tab
                  value="avoided"
                  label={t("ghg.sankeyPanel.tabs.avoided", "Avoided emissions ({{unit}})", {
                    unit: unitLabel,
                  })}
                />
              </Tabs>

              {/* Quantity Sankey */}
              <Box
                ref={sankeyQtyRef}
                sx={{
                  height: { xs: 480, md: 540, lg: 600 },
                  display: active === "qty" ? "block" : "none",
                }}
              >
                <ResponsiveContainer width="95%" height="100%">
                  <Sankey
                    data={qtyDataReversed}
                    node={<SankeyNode />}
                    link={<SankeyLink />}
                    nodePadding={20}
                    nodeWidth={12}
                    iterations={36}
                    margin={{ top: 20, bottom: 20, left: 90, right: 90 }}
                  >
                    <RTooltip
                      formatter={(value) => round2(value)}
                      contentStyle={{ fontSize: 13, borderRadius: 8 }}
                    />
                  </Sankey>
                </ResponsiveContainer>
              </Box>

              {/* Avoided Sankey */}
              <Box
                ref={sankeyAvoidedRef}
                sx={{
                  height: { xs: 480, md: 540, lg: 600 },
                  display: active === "avoided" ? "block" : "none",
                }}
              >
                <ResponsiveContainer width="95%" height="100%">
                  <Sankey
                    data={avoidedDataReversed}
                    node={<SankeyNode />}
                    link={<SankeyLink />}
                    nodePadding={20}
                    nodeWidth={12}
                    iterations={36}
                    margin={{ top: 20, bottom: 20, left: 90, right: 90 }}
                  >
                    <RTooltip
                      formatter={(value) => round2(value)}
                      contentStyle={{ fontSize: 13, borderRadius: 8 }}
                    />
                  </Sankey>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        )}
      </RxGhgAccordion>
    </Box>
  );
}